/**
 * PRDEditor Component
 *
 * Edit PRD sections with auto-save and validation
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Save, X, Plus, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import { usePRD, useUpdatePRD } from '@/hooks/usePRD';
import { useToast } from '@/shared/hooks/useToast';
import type {
  PRD,
  PRDStatus,
  PRDPriority,
  UpdatePRDRequest,
  PRDGoal,
  PRDMetric,
} from '@forgenetwork/types';

interface PRDEditorProps {
  prdId: string;
  onSave?: () => void;
  onCancel?: () => void;
}

/**
 * PRDEditor - Edit PRD sections with auto-save
 */
export function PRDEditor({ prdId, onSave, onCancel }: PRDEditorProps): React.JSX.Element {
  const { data, isLoading, error } = usePRD(prdId);
  const updatePRD = useUpdatePRD();
  const { toast } = useToast();

  const [editedPRD, setEditedPRD] = React.useState<Partial<PRD> | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);

  // Initialize edited PRD when data loads
  React.useEffect(() => {
    if (data?.prd && !editedPRD) {
      setEditedPRD(data.prd);
    }
  }, [data, editedPRD]);

  const handleFieldChange = <K extends keyof PRD>(field: K, value: PRD[K]): void => {
    setEditedPRD((prev) => (prev ? { ...prev, [field]: value } : null));
    setIsDirty(true);
  };

  const handleSave = React.useCallback((): void => {
    if (!editedPRD) return;

    const updates: UpdatePRDRequest = {
      title: editedPRD.title,
      status: editedPRD.status,
      sections: editedPRD.sections,
      features: editedPRD.features,
      goals: editedPRD.goals,
      metrics: editedPRD.metrics,
      dependencies: editedPRD.dependencies,
    };

    updatePRD.mutate(
      { prdId, updates },
      {
        onSuccess: () => {
          setIsDirty(false);
          toast({
            title: 'PRD Saved',
            description: 'Your changes have been saved successfully.',
          });
          onSave?.();
        },
        onError: (error: Error) => {
          toast({
            title: 'Save Failed',
            description: error.message || 'Failed to save changes',
            variant: 'destructive',
          });
        },
      }
    );
  }, [editedPRD, prdId, updatePRD, toast, onSave]);

  // Auto-save with debounce
  const autoSaveTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  React.useEffect(() => {
    if (isDirty && editedPRD) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 2000); // Auto-save after 2 seconds of inactivity
    }
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [isDirty, editedPRD, handleSave]);

  const handleCancel = (): void => {
    if (data?.prd) {
      setEditedPRD(data.prd);
      setIsDirty(false);
    }
    onCancel?.();
  };

  // Goal management
  const handleAddGoal = (): void => {
    if (!editedPRD) return;
    const newGoal: PRDGoal = {
      id: `goal-${Date.now()}`,
      description: '',
      order: (editedPRD.goals?.length ?? 0) + 1,
    };
    handleFieldChange('goals', [...(editedPRD.goals ?? []), newGoal]);
  };

  const handleUpdateGoal = (goalId: string, description: string): void => {
    if (!editedPRD) return;
    const updated = editedPRD.goals?.map((g) =>
      g.id === goalId ? { ...g, description } : g
    );
    handleFieldChange('goals', updated);
  };

  const handleRemoveGoal = (goalId: string): void => {
    if (!editedPRD) return;
    handleFieldChange(
      'goals',
      editedPRD.goals?.filter((g) => g.id !== goalId)
    );
  };

  // Metric management
  const handleAddMetric = (): void => {
    if (!editedPRD) return;
    const newMetric: PRDMetric = {
      id: `metric-${Date.now()}`,
      name: '',
      description: '',
      order: (editedPRD.metrics?.length ?? 0) + 1,
    };
    handleFieldChange('metrics', [...(editedPRD.metrics ?? []), newMetric]);
  };

  const handleUpdateMetric = (
    metricId: string,
    updates: Partial<PRDMetric>
  ): void => {
    if (!editedPRD) return;
    const updated = editedPRD.metrics?.map((m) =>
      m.id === metricId ? { ...m, ...updates } : m
    );
    handleFieldChange('metrics', updated);
  };

  const handleRemoveMetric = (metricId: string): void => {
    if (!editedPRD) return;
    handleFieldChange(
      'metrics',
      editedPRD.metrics?.filter((m) => m.id !== metricId)
    );
  };

  // Feature priority update
  const handleUpdateFeaturePriority = (
    featureId: string,
    priority: PRDPriority
  ): void => {
    if (!editedPRD) return;
    const updated = editedPRD.features?.map((f) =>
      f.id === featureId ? { ...f, priority } : f
    );
    handleFieldChange('features', updated);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
        <div className="h-64 bg-muted animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error instanceof Error ? error.message : 'Failed to load PRD'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!editedPRD) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">PRD not found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Edit PRD</CardTitle>
            <div className="flex items-center gap-2">
              {isDirty && (
                <Badge variant="secondary" className="gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Unsaved Changes
                </Badge>
              )}
              {updatePRD.isPending && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={updatePRD.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isDirty || updatePRD.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={editedPRD.title ?? ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="PRD Title"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={editedPRD.status ?? 'draft'}
              onValueChange={(value) => handleFieldChange('status', value as PRDStatus)}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader>
          <CardTitle>Sections</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible>
            {editedPRD.sections
              ?.sort((a, b) => a.order - b.order)
              .map((section) => (
                <AccordionItem key={section.id} value={section.id}>
                  <AccordionTrigger>{section.title}</AccordionTrigger>
                  <AccordionContent>
                    <Textarea
                      value={section.content}
                      onChange={(e) => {
                        const updated = editedPRD.sections?.map((s) =>
                          s.id === section.id ? { ...s, content: e.target.value } : s
                        );
                        if (updated) {
                          handleFieldChange('sections', updated);
                        }
                      }}
                      rows={8}
                      className="font-mono text-sm"
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Goals */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Goals</CardTitle>
            <Button size="sm" onClick={handleAddGoal}>
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {editedPRD.goals
            ?.sort((a, b) => a.order - b.order)
            .map((goal) => (
              <div key={goal.id} className="flex items-start gap-2">
                <Textarea
                  value={goal.description}
                  onChange={(e) => handleUpdateGoal(goal.id, e.target.value)}
                  placeholder="Goal description"
                  rows={2}
                  className="flex-1"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleRemoveGoal(goal.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
        </CardContent>
      </Card>

      {/* Success Metrics */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Success Metrics</CardTitle>
            <Button size="sm" onClick={handleAddMetric}>
              <Plus className="h-4 w-4 mr-2" />
              Add Metric
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {editedPRD.metrics
            ?.sort((a, b) => a.order - b.order)
            .map((metric) => (
              <div key={metric.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <Input
                    value={metric.name}
                    onChange={(e) =>
                      handleUpdateMetric(metric.id, { name: e.target.value })
                    }
                    placeholder="Metric name"
                    className="flex-1"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemoveMetric(metric.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  value={metric.description}
                  onChange={(e) =>
                    handleUpdateMetric(metric.id, { description: e.target.value })
                  }
                  placeholder="Metric description"
                  rows={2}
                />
                <Input
                  value={metric.target ?? ''}
                  onChange={(e) =>
                    handleUpdateMetric(metric.id, { target: e.target.value })
                  }
                  placeholder="Target (optional)"
                />
              </div>
            ))}
        </CardContent>
      </Card>

      {/* Features (read-only with priority editing) */}
      {editedPRD.features && editedPRD.features.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Features (Priority Assignment)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {editedPRD.features.map((feature) => (
              <div key={feature.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold">{feature.name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {feature.description}
                    </p>
                  </div>
                  <Select
                    value={feature.priority}
                    onValueChange={(value) =>
                      handleUpdateFeaturePriority(feature.id, value as PRDPriority)
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="P0">P0</SelectItem>
                      <SelectItem value="P1">P1</SelectItem>
                      <SelectItem value="P2">P2</SelectItem>
                      <SelectItem value="P3">P3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
